import { h, createContext } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import CartService, { CART_EVENTS } from './cart-service';
import { getItemsToUpdate } from './upsells-service';
import { combineBundles, getBundlesList } from './bundles-service';

const Context = createContext();

const getInvalidBundles = (cartState) =>
  cartState.items?.filter((item) => {
    // eslint-disable-next-line no-prototype-builtins
    if (item.properties._bundle_data && item.hasOwnProperty('has_components') && item.has_components === false) {
      return true;
    }
    return false;
  });

const CartContext = ({ children, data }) => {
  const [cartState, setCartState] = useState(CartService.cart);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(CartService.loading);
  const [upsells, setUpsells] = useState([]);
  const [errors, setErrors] = useState([]);

  useEffect(async () => {
    const listener = (event) => {
      setLoading(CartService.loading);
      setCartState(CartService.cart);
      if (event === CART_EVENTS.UPDATE) {
        setErrors([]);
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
    };
    getBundlesList().then((response) => {
      setBundles(response);
    });
    CartService.addListener(listener);
    setCartState(CartService.cart);

    return () => {
      CartService.removeListener(listener);
    };
  }, []);

  useEffect(async () => {
    const cartCountEls = document.querySelectorAll('[data-cart-count]');
    const cartCount = cartState?.items?.reduce((acc, item) => acc + item.quantity, 0);

    // Find the cart count span inside the button (for text display)
    const cartCountSpan = document.querySelector('button[data-header-cart-trigger] [data-cart-count]');
    if (cartCountSpan) {
      cartCountSpan.textContent = cartCount || '';
    }

    // Set attribute on all elements with data-cart-count
    Array.from(cartCountEls).forEach((el) => {
      el.setAttribute('data-cart-count', cartCount || '0');
    });

    const invalidBundles = getInvalidBundles(cartState);
    if (invalidBundles?.length) {
      setErrors([window.TMW.Strings.cart.maxBundleLimit]);
      const lastBundle = invalidBundles.pop();
      CartService.update(
        [
          {
            id: lastBundle.key,
            quantity: 0,
          },
        ],
        true,
      ).then(() => {
        CartService.get(true);
      });
    }
  }, [cartState]);

  useEffect(() => {
    const upsellItems = getItemsToUpdate(upsells, cartState);

    if (upsellItems.length > 0 && cartState.items.length > 0) {
      CartService.update(upsellItems, true);
    }
  }, [upsells, cartState]);

  useEffect(() => {
    const bundleChanges = combineBundles(bundles, cartState);

    if (bundleChanges?.updates || bundleChanges?.add) {
      CartService.update(bundleChanges.updates, true).finally(() => {
        CartService.add(bundleChanges.add, true);
      });
    }
  }, [bundles, cartState]);
  /**
   * Wrapper for cart API to make interactions more simple
   */
  const cart = {
    add: (...params) => CartService.add(...params),
    remove: (...params) => CartService.remove(...params),
    update: (...params) => CartService.update(...params),
    state: cartState,
    loading,
    errors,
  };

  return (
    <Context.Provider
      value={{
        cart,
        cartLoading: loading,
        ...data,
        upsells,
        setUpsells,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const withCartContext = (Rendered) => (props) =>
  <Context.Consumer>{(value) => <Rendered {...props} {...value} />}</Context.Consumer>;

export default CartContext;
